import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowCounterClockwise, CaretRight, FlagCheckered, ShareNetwork } from '@phosphor-icons/react';
import telegramService from '../../services/telegram';
import useNavOffset from '../../hooks/useNavOffset';
import { ConfirmSheet } from './WalkScreen';

/**
 * Финиш прогулки — общий для маршрута редакции и своего маршрута.
 *
 * - `results` — итоги `[{ value, label }]`, крупными цифрами без плашек;
 * - `stops` — места в общем виде, как у `WalkScreen`: ниже кнопок —
 *   список, чтобы вернуться к понравившемуся;
 * - `secondary` — `{ label, onClick }`: «Другие маршруты» или «Мои маршруты»;
 * - `onRestart` — после подтверждения в окне.
 */
const WalkFinish = ({ title, results, stops, onShare, secondary, onRestart }) => {
  const navigate = useNavigate();
  const navOffset = useNavOffset();
  const [showRestart, setShowRestart] = useState(false);

  // Убираем уведомление о прошлом месте: оно поднято над панелью
  // прогулки, а на финише панели нет, и тост висел посреди итогов
  useEffect(() => { toast.dismiss(); }, []);

  return (
    <div
      className="min-h-screen bg-theme-bg px-4"
      style={{
        // Telegram уже кладёт в content-safe-area-top зазор под своими
        // кнопками — свои 40px сверху давали пустую полосу в пол-экрана
        paddingTop: 'calc(var(--safe-area-top, 0px) + var(--content-safe-area-top, 0px) + 8px)',
        paddingBottom: navOffset + 24,
      }}
    >
      <div className="mx-auto flex max-w-md flex-col items-center text-center">
        {/* Знак финиша — лаймом, акцентом бренда: это праздник, а не
            служебная иконка. На лайме только тёмный глиф (брендбук 2.4) */}
        <div className="flex h-[72px] w-[72px] items-center justify-center rounded-full bg-lime">
          <FlagCheckered size={36} weight="fill" color="#16181D" />
        </div>

        <h1 className="m-0 mt-4 font-display text-h1 text-theme-text-primary">Маршрут пройден</h1>
        <p className="m-0 mt-1 text-body text-theme-text-secondary">{title}</p>

        {/* Итоги — крупными цифрами без плашек: иерархию держит размер,
            а не рамки (брендбук 1) */}
        <div className="mt-5 grid w-full grid-cols-2 gap-x-4 gap-y-4 border-y border-theme-border py-4">
          {results.map((r) => (
            <div key={r.label}>
              <div className="font-display text-h2 text-theme-text-primary tabular">{r.value}</div>
              <div className="mt-1 text-caption text-theme-text-secondary">{r.label}</div>
            </div>
          ))}
        </div>

        <p className="m-0 mt-3 text-caption text-theme-text-secondary">
          {new Date().toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })}
        </p>

        <div className="mt-5 flex w-full flex-col gap-2">
          <button
            onClick={onShare}
            className="flex h-14 w-full items-center justify-center gap-2 rounded-md border-0 bg-action text-label text-on-action transition-transform duration-fast ease-out active:scale-98"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <ShareNetwork size={20} />
            Поделиться результатом
          </button>
          <button
            onClick={() => { telegramService.hapticFeedback('light'); secondary.onClick(); }}
            className="flex h-12 w-full items-center justify-center rounded-md border-0 bg-theme-card-secondary text-label text-action-text transition-transform duration-fast ease-out active:scale-98"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            {secondary.label}
          </button>
          {/* Пройти заново — редкое действие: тихой текстовой кнопкой */}
          <button
            onClick={() => { telegramService.hapticFeedback('light'); setShowRestart(true); }}
            className="mt-2 flex h-11 w-full items-center justify-center gap-2 border-0 bg-transparent text-label text-theme-text-secondary"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <ArrowCounterClockwise size={20} />
            Пройти заново
          </button>
        </div>

        {/* Места маршрута — вернуться к понравившемуся после прогулки.
            Строка ведёт на страницу места */}
        {stops.some((s) => s.slug) && (
          <section className="mt-6 w-full text-left">
            <h2 className="mb-2 font-display text-h3 text-theme-text-primary">Места маршрута</h2>
            <ol className="m-0 list-none p-0">
              {stops.map((stop, i) => {
                if (!stop.slug) return null;
                return (
                  <li key={stop.key} className="border-b border-theme-border last:border-b-0">
                    <button
                      onClick={() => { telegramService.hapticFeedback('light'); navigate(`/place/${stop.slug}`); }}
                      className="flex min-h-[64px] w-full items-center gap-3 border-0 bg-transparent px-0 py-2 text-left transition-transform duration-instant ease-out active:scale-98"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      <div className="w-10 shrink-0 overflow-hidden rounded-md bg-theme-card-secondary" style={{ aspectRatio: '3 / 4' }}>
                        {stop.thumb && <img src={stop.thumb} alt="" loading="lazy" className="h-full w-full object-cover" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-body text-theme-text-primary">{stop.title}</div>
                        <div className="text-caption text-theme-text-secondary tabular">
                          {`Место ${i + 1}`}{stop.status === 'skipped' ? ' · пропущено' : ''}
                        </div>
                      </div>
                      <CaretRight size={20} className="shrink-0 text-theme-text-hint" />
                    </button>
                  </li>
                );
              })}
            </ol>
          </section>
        )}
      </div>

      <ConfirmSheet
        isOpen={showRestart}
        onClose={() => setShowRestart(false)}
        title="Начать заново?"
        action="Начать заново"
        danger
        onAction={() => { setShowRestart(false); onRestart(); }}
      >
        Весь прогресс и результат маршрута будут сброшены. Это действие нельзя отменить.
      </ConfirmSheet>
    </div>
  );
};

export default WalkFinish;
